/**
 * Shared helper utilities for React Query hooks
 *
 * Centralizes common functionality across all API queries to ensure consistency
 * and eliminate code duplication. This file can be extended with additional
 * helper functions as needed.
 */

/**
 * Creates a retry handler for React Query hooks
 *
 * @param includeNotFound - Whether to include 404 errors in the "don't retry" list
 * @returns A retry function that can be used in useQuery configurations
 *
 * @example
 * // For list endpoints (don't retry on 404)
 * retry: createRetryHandler(false)
 *
 * // For detail endpoints (don't retry on 404)
 * retry: createRetryHandler(true)
 */
export const createRetryHandler =
  (includeNotFound = false) =>
  (failureCount: number, error: unknown) => {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      const shouldNotRetry =
        status === 401 || status === 403 || (includeNotFound && status === 404);
      if (shouldNotRetry) {
        return false;
      }
    }
    return failureCount < 2;
  };

/**
 * Pre-configured retry handlers for common use cases
 */
export const retryHandlers = {
  /**
   * For list endpoints - don't retry on auth errors only
   */
  list: createRetryHandler(false),

  /**
   * For detail endpoints - don't retry on auth errors and 404
   */
  detail: createRetryHandler(true),

  /**
   * For endpoints backed by a rate-limited upstream (KIR-4887): a 503 means
   * "temporarily unavailable, back off", so retry it at most once instead of
   * adding load. Other errors behave like `list`.
   */
  // `error: Error` (TanStack's default error type), not `unknown`, so passing
  // this as `retry` keeps the query's inferred error type as `Error`.
  rateLimited: (failureCount: number, error: Error): boolean => {
    if ('status' in error && (error as { status: unknown }).status === 503) {
      return failureCount < 1;
    }
    return createRetryHandler(false)(failureCount, error);
  },
} as const;
